import React from 'react';
import { Loader2, Inbox } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

// Page header with title, subtitle and optional actions
export const PageHeader = ({ title, subtitle, actions, icon: Icon }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-primary" />
        </div>
      )}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

// Stat card with left accent bar
export const StatCard = ({ label, value, icon: Icon, accent = 'students', testId }) => (
  <div
    className="bg-card border border-border rounded-md shadow-[var(--shadow-tight)] hover:border-primary/40 transition-colors duration-200 flex items-stretch overflow-hidden"
    data-testid={testId}
  >
    <div className={`w-1.5 accent-${accent}`} />
    <div className="p-4 flex items-center justify-between gap-3 flex-1">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-semibold tabular-nums mt-1 text-foreground">{value}</p>
      </div>
      {Icon && (
        <div className={`w-11 h-11 rounded-md flex items-center justify-center accent-${accent} bg-opacity-10`} style={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
          <Icon className="w-5 h-5 text-foreground/70" />
        </div>
      )}
    </div>
  </div>
);

export const LoadingState = ({ rows = 5 }) => (
  <div className="space-y-3" data-testid="loading-state">
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} className="h-12 w-full rounded-md" />
    ))}
  </div>
);

export const InlineSpinner = ({ label }) => (
  <div className="flex items-center justify-center py-10 text-muted-foreground">
    <Loader2 className="w-5 h-5 animate-spin mr-2" />
    {label || 'Loading...'}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, description, actionLabel, onAction, testId }) => (
  <div className="flex flex-col items-center justify-center text-center py-14 px-6" data-testid={testId || 'empty-state'}>
    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
      <Icon className="w-7 h-7 text-muted-foreground" />
    </div>
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    {description && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{description}</p>}
    {actionLabel && onAction && (
      <Button className="mt-4" onClick={onAction} data-testid="empty-state-action">
        {actionLabel}
      </Button>
    )}
  </div>
);

// Simple sortable-less data table wrapper
export const Card = ({ children, className = '' }) => (
  <div className={`bg-card border border-border rounded-md shadow-[var(--shadow-tight)] ${className}`}>{children}</div>
);
